import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Search, Eye, Edit2, Trash2, Building, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

import { UserListSkeleton } from '../../components/SkeletonComponents/UserListSkeleton';
import DesignationForm from '../../components/HRComponents/DesignationForm';
import ModernTable from '../../components/ui/ModernTable';
import api from '../../common/apiClient';
import SummaryApi from '../../common/SummaryApi';

const DesignationList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Form State
  const [formConfig, setFormConfig] = useState({
    isOpen: false,
    mode: 'create', // create, edit, view
    selectedData: null
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [desgRes, deptRes, divRes] = await Promise.all([
        api.get(SummaryApi.getAllDesignations.url),
        api.get(SummaryApi.getAllDepartments.url),
        api.get(SummaryApi.getAllDivisions.url)
      ]);
      if (desgRes.data.success) setData(desgRes.data.data || []);
      if (deptRes.data.success) setDepartments(deptRes.data.data || []);
      if (divRes.data.success) setDivisions(divRes.data.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const response = await api.delete(SummaryApi.deleteDesignation.url(id));
        if (response.data.success) {
          toast.success('Designation deleted');
          fetchData();
        }
      } catch (error) {
        toast.error('Error deleting designation');
      }
    }
  };

  const filteredData = useMemo(() => {
    return data.filter(item => 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.designationId?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  const columns = [
    {
      label: 'Designation',
      key: 'title',
      render: (row) => (
        <div>
          <div className="font-semibold text-gray-900">{row.title}</div>
          <div className="text-xs text-gray-500">{row.designationId}</div>
        </div>
      )
    },
    {
      label: 'Level',
      key: 'level',
      render: (row) => <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-bold">Grade {row.level}</span>
    },
    {
      label: 'Dept / Div',
      key: 'department',
      render: (row) => (
        <div className="text-xs">
          <div className="flex items-center"><Building className="h-3 w-3 mr-1 text-gray-400" /> {row.departmentId?.name || 'N/A'}</div>
          <div className="flex items-center text-gray-500"><Layers className="h-3 w-3 mr-1 text-gray-400" /> {row.divisionId?.name || 'N/A'}</div>
        </div>
      )
    },
    {
        label: 'Description',
        key: 'description',
        render: (row) => <div className="text-xs text-gray-500 truncate max-w-[200px]">{row.description || '-'}</div>
    }
  ];

  const actions = [
    { label: 'View', icon: <Eye className="h-4 w-4" />, onClick: (row) => setFormConfig({ isOpen: true, mode: 'view', selectedData: row }) },
    { label: 'Edit', icon: <Edit2 className="h-4 w-4" />, onClick: (row) => setFormConfig({ isOpen: true, mode: 'edit', selectedData: row }) },
    { label: 'Delete', icon: <Trash2 className="h-4 w-4 text-red-500" />, onClick: (row) => handleDelete(row._id) }
  ];

  if (loading) return <UserListSkeleton />;

  return (
    <div className="p-2">
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="rounded-lg bg-red-50 border border-red-100 hover:border-primary text-textPrimary hover:text-primary p-2 transition-colors"><FaArrowLeft /></button>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Designations</h1>
        </div>
        <button 
          onClick={() => setFormConfig({ isOpen: true, mode: 'create', selectedData: null })}
          className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
        >
          <Plus className="h-5 w-5" /> Add Designation
        </button>
      </div>

      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
        <input 
          type="text" placeholder="Search designations..." 
          className="w-full pl-10 pr-4 py-2 border rounded-xl"
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <ModernTable 
        columns={columns} 
        data={paginatedData} 
        actions={actions}
        pagination={{
            totalItems: filteredData.length,
            itemsPerPage,
            currentPage,
            onPageChange: setCurrentPage,
            onItemsPerPageChange: setItemsPerPage
        }}
      />

      <DesignationForm 
        isOpen={formConfig.isOpen}
        onClose={() => setFormConfig({ ...formConfig, isOpen: false })}
        onSuccess={fetchData}
        isEditMode={formConfig.mode === 'edit'}
        isViewMode={formConfig.mode === 'view'}
        initialData={formConfig.selectedData}
        departments={departments}
        divisions={divisions}
      />
    </div>
  );
};

export default DesignationList;